import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ArticlesList from '../../features/articles/ArticlesList';
import SitesList from '../../features/sites/SitesList';
import SiteDetails from '../../features/sites/SiteDetails';
import Dashboard from '../../features/home/Dashboard';
import GoodsToSiteForm from '../../features/goods/GoodsToSiteForm/GoodsToSiteForm';
import ClientList from '../../features/clients/ClientList';
import GoodsList from '../../features/goods/GoodsList';
import GoodsByInvoiceList from '../../features/goods/GoodsByInvoiceList';
import ClientDetails from '../../features/clients/ClientDetails';
import OrdersList from '../../features/orders/OrdersList';
import PurchaseOrdersList from '../../features/purchaseOrders/PurchaseOrdersList';
import { useAppSelector } from '../../hooks/hooks';
import {
    isEmployeeProcurementManager,
    isEmployeeProjectManager,
    isEmployeeFinanceManager,
    isEmployeeWarehouseManager,
    isEmployeeWarehouseOwner,
    isEmployeeWarehouseOwnerOnly,
    isEmployeeSystemAdmin,
    isEmployeeProjectAdmin,
    canSeeProductTemplates,
} from '../../redux/selectors/employeeSelector';
import OrdersDetails from '../../features/orders/OrderDetails';
import WaybillReport from '../../features/reports/WaybillReport';
import CompanyDetailsSettings from '../../features/companyDetails/CompanyDetailsSettings';
import ProjectsList from '../../features/projects/ProjectsList';
import ProjectDetails from '../../features/projects/ProjectDetails';
import ApiTokensPage from '../../features/apiTokens/ApiTokensPage';
import ProductTemplatesList from '../../features/productTemplates/ProductTemplatesList';
import MyTasksPage from '../../features/tasks/MyTasksPage';
import { useIsMobile } from '../../common/components/mobile/MobilePrimitives';

export default function AppRoutes() {
    const isWarehouseOwner = useAppSelector(isEmployeeWarehouseOwner);
    const isWarehouseManager = useAppSelector(isEmployeeWarehouseManager);
    const isProjectManager = useAppSelector(isEmployeeProjectManager);
    const isProjectAdmin = useAppSelector(isEmployeeProjectAdmin);
    const isProcurementManager = useAppSelector(isEmployeeProcurementManager);
    const isSystemAdmin = useAppSelector(isEmployeeSystemAdmin);
    const isFinanceManager = useAppSelector(isEmployeeFinanceManager);
    const isHighestPrivilageWarehouseOwner = useAppSelector(isEmployeeWarehouseOwnerOnly);
    const canSeeTemplates = useAppSelector(canSeeProductTemplates);
    const isMobile = useIsMobile();

    return (
        <React.Fragment>
            {/* A <Switch> looks through its children <Route>s and
								renders the first one that matches the current URL. */}
            <Routes>
                {(isProjectManager || isWarehouseManager) && (
                    <Route path="/articles">
                        <Route index element={<ArticlesList />} />
                    </Route>
                )}
                {(isProjectManager || isWarehouseManager || isFinanceManager) && (
                    <Route path="/goods">
                        <Route index element={<GoodsList />} />
                        <Route path=":siteId">
                            <Route path="goodsToSiteForm" element={<GoodsToSiteForm />} />
                        </Route>
                    </Route>
                )}
                {(isProjectManager || isWarehouseManager || isFinanceManager) && (
                    <Route path="/goodsByInvoice" element={<GoodsByInvoiceList />} />
                )}
                {(isWarehouseManager || isWarehouseOwner || isFinanceManager) && (
                    <Route path="/warehouses">
                        <Route index element={<SitesList />} />
                        <Route path=":siteId">
                            <Route index element={<SiteDetails />} />
                            {!isHighestPrivilageWarehouseOwner && (
                                <Route path="goodsToSite" element={<GoodsToSiteForm />} />
                            )}
                        </Route>
                    </Route>
                )}
                {(isProjectManager || isWarehouseManager || isWarehouseOwner || isFinanceManager) && (
                    <Route path="/constructionSites">
                        <Route index element={<SitesList />} />
                        <Route path=":siteId">
                            <Route index element={<SiteDetails />} />
                            {!isHighestPrivilageWarehouseOwner && (
                                <Route path="goodsToSite" element={<GoodsToSiteForm />} />
                            )}
                        </Route>
                    </Route>
                )}
                {(isProjectAdmin || isProjectManager || isFinanceManager) && (
                    <Route path="/customers">
                        <Route path=":clientId">
                            <Route index element={<ClientDetails />} />
                        </Route>
                        <Route index element={<ClientList />} />
                    </Route>
                )}
                {(isProjectAdmin || isProjectManager || isFinanceManager) && (
                    <Route path="/projects">
                        <Route path=":projectId">
                            <Route index element={<ProjectDetails />} />
                        </Route>
                        <Route index element={<ProjectsList />} />
                    </Route>
                )}

                {(isProjectAdmin || isProcurementManager || isFinanceManager) && (
                    <Route path="/suppliers">
                        <Route path=":clientId">
                            <Route index element={<ClientDetails />} />
                        </Route>
                        <Route index element={<ClientList />} />
                    </Route>
                )}

                {(isProjectManager || isWarehouseManager || isWarehouseOwner) && (
                    <Route path="/tasks" element={<MyTasksPage />} />
                )}
                {(isProjectManager || isWarehouseManager || isWarehouseOwner) && (
                    <Route path="/orders">
                        <Route index element={<OrdersList />} />
                        <Route path="demandOrder">
                            <Route path=":demandOrderId" element={<OrdersDetails />} />
                        </Route>
                        <Route path="returnOrder">
                            <Route path=":returnOrderId" element={<OrdersDetails />} />
                        </Route>
                    </Route>
                )}

                {(isProcurementManager || isWarehouseOwner) && (
                    <Route path="/purchaseOrders">
                        <Route index element={<PurchaseOrdersList />} />
                    </Route>
                )}

                {canSeeTemplates && <Route path="/productTemplates" element={<ProductTemplatesList />} />}

                <Route path={'waybillPrint/:waybillId'} element={<WaybillReport />} />
                {isFinanceManager && <Route path="/" element={<Dashboard />} />}
                {(isProjectManager || isWarehouseManager || isWarehouseOwner) && (
                    <Route path="/" element={<Navigate to={isMobile ? '/tasks' : '/constructionSites'} replace />} />
                )}
                {isProcurementManager && <Route path="/" element={<Navigate to="/purchaseOrders" replace />} />}
                {isProjectAdmin && <Route path="/" element={<Navigate to="/projects" replace />} />}
                {isSystemAdmin && <Route path="settings" element={<CompanyDetailsSettings />} />}
                {isSystemAdmin && <Route path="apiTokens" element={<ApiTokensPage />} />}
            </Routes>
        </React.Fragment>
    );
}
